import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Clock, CheckCircle2, XCircle, Star, Loader2, Building2, Pencil, Power, X } from "lucide-react";

const CITIES = ["Campinas", "Hortolândia", "Sumaré", "Valinhos", "Vinhedo", "Paulínia", "Americana", "Indaiatuba"];
const TIPOS = ["CLT", "PJ", "Temporário", "Estágio", "Freelancer", "Meio período"];

function ProfileForm({ user, onSaved }) {
  const [form, setForm] = useState({
    company_name: user.company_name || "",
    whatsapp: user.whatsapp || "",
    cidade: user.cidade || "",
    about: user.about || "",
  });
  const [saving, setSaving] = useState(false);
  const locked = user.approved;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/company/profile", form);
      toast.success(locked ? "Alterações salvas. Mudança de nome/cidade passa por aprovação do admin." : "Dados da empresa salvos!");
      onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-3xl p-8 border border-slate-200 max-w-2xl">
      <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-950 flex items-center gap-2">
        <Building2 size={22} /> Dados da empresa
      </h2>
      <p className="text-slate-600 mt-1 mb-6">
        {locked ? "Nome e cidade só mudam após aprovação do administrador. WhatsApp e descrição são atualizados na hora." : "Complete o cadastro para começar a publicar vagas."}
      </p>
      <div className="grid gap-4">
        <input data-testid="profile-name" required placeholder="Nome da empresa" value={form.company_name}
          onChange={(e) => setForm({ ...form, company_name: e.target.value })}
          className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" />
        <input data-testid="profile-whatsapp" required placeholder="WhatsApp com DDD (ex: 19 99999-9999)" value={form.whatsapp}
          onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
          className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" />
        <select data-testid="profile-city" required value={form.cidade}
          onChange={(e) => setForm({ ...form, cidade: e.target.value })}
          className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500">
          <option value="">Cidade principal</option>
          {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <textarea data-testid="profile-about" placeholder="Sobre a empresa (opcional)" value={form.about}
          onChange={(e) => setForm({ ...form, about: e.target.value })} rows={3}
          className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" />
        <button data-testid="profile-save" disabled={saving}
          className="bg-emerald-400 text-slate-950 font-extrabold py-3 rounded-xl hover:bg-emerald-500 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {saving ? <Loader2 className="animate-spin" size={18} /> : "Salvar dados"}
        </button>
      </div>
    </form>
  );
}

function ChangeRequestModal({ job, onClose, onDone }) {
  const [form, setForm] = useState({
    cargo: job.cargo, cidade: job.cidade, tipo: job.tipo, salario: job.salario,
    descricao: job.descricao, whatsapp: job.whatsapp || "", email: job.email || "", url: job.url || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/company/jobs/${job.id}/change-request`, form);
      toast.success("Solicitação enviada ao administrador. A data de expiração não muda.");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao solicitar alteração.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/50 flex items-center justify-center p-4" data-testid="change-request-modal">
      <div className="bg-white rounded-3xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-heading text-xl font-extrabold text-slate-950">Solicitar alteração</h3>
          <button onClick={onClose} data-testid="close-modal" className="text-slate-400 hover:text-slate-950"><X size={22} /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Mudar o cargo caracteriza uma nova oportunidade — publique uma nova vaga. O prazo de expiração é mantido.
        </p>
        <form onSubmit={submit} className="grid gap-3">
          <input data-testid="cr-cargo" disabled value={form.cargo}
            className="w-full rounded-xl px-4 py-3 border border-slate-200 bg-slate-50 text-slate-500" />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="cr-cidade" value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })}
              className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500">
              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="cr-tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
              className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500">
              {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <input data-testid="cr-salario" value={form.salario} onChange={(e) => setForm({ ...form, salario: e.target.value })}
            className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" placeholder="Salário" />
          <textarea data-testid="cr-descricao" rows={4} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" placeholder="Descrição" />
          <input data-testid="cr-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
            className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" placeholder="WhatsApp" />
          <input data-testid="cr-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" placeholder="E-mail (opcional)" />
          <input data-testid="cr-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
            className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" placeholder="Link (opcional)" />
          <button data-testid="cr-submit" disabled={saving}
            className="bg-emerald-400 text-slate-950 font-extrabold py-3 rounded-xl hover:bg-emerald-500 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Enviar solicitação"}
          </button>
        </form>
      </div>
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const map = {
    pending: ["bg-amber-100 text-amber-800", Clock, "Em análise"],
    approved: ["bg-emerald-100 text-emerald-800", CheckCircle2, "Publicada"],
    rejected: ["bg-red-100 text-red-800", XCircle, "Reprovada"],
    closed: ["bg-slate-200 text-slate-700", Power, "Encerrada"],
  };
  const [cls, Icon, label] = map[status] || map.pending;
  return (
    <span className={`text-xs font-extrabold px-3 py-1 rounded-full flex items-center gap-1 ${cls}`}>
      <Icon size={12} /> {label}
    </span>
  );
};

export default function CompanyDashboard() {
  const { user, setUser } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editJob, setEditJob] = useState(null);

  const load = useCallback(async () => {
    try {
      const [j, s] = await Promise.all([api.get("/company/jobs"), api.get("/company/summary")]);
      setJobs(j.data);
      setSummary(s.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Excluir esta vaga?")) return;
    await api.delete(`/company/jobs/${id}`);
    toast.success("Vaga excluída.");
    load();
  };
  const close = async (id) => {
    if (!window.confirm("Encerrar esta vaga? Ela deixará de aparecer no site.")) return;
    await api.post(`/company/jobs/${id}/close`);
    toast.success("Vaga encerrada.");
    load();
  };

  if (!user.profile_complete) {
    return (
      <div className="max-w-7xl mx-auto px-5 py-12">
        <ProfileForm user={user} onSaved={(u) => { setUser(u); load(); }} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 py-12">
      {editJob && <ChangeRequestModal job={editJob} onClose={() => setEditJob(null)} onDone={() => { setEditJob(null); load(); }} />}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-950">Painel — {user.company_name}</h1>
          <p className="text-slate-600 mt-1">Gerencie suas vagas publicadas.</p>
        </div>
        <Link to="/empresa/nova-vaga" data-testid="new-job-button"
          className="bg-emerald-400 text-slate-950 font-extrabold px-5 py-3 rounded-xl hover:bg-emerald-500 hover:-translate-y-1 transition-all duration-200 flex items-center gap-2 self-start">
          <Plus size={18} /> Publicar vaga
        </Link>
      </div>

      {!summary?.approved && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8 text-amber-900" data-testid="pending-approval">
          <strong>Empresa em análise.</strong> Seu cadastro está aguardando aprovação do administrador. Você poderá publicar vagas assim que for aprovado.
        </div>
      )}
      {summary?.pending_profile && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-8 text-blue-900" data-testid="pending-profile">
          <strong>Alteração de dados em análise.</strong> Sua mudança de nome/cidade aguarda aprovação do administrador.
        </div>
      )}

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <div className="bg-white rounded-2xl p-5 border border-slate-200">
          <p className="text-slate-500 font-bold text-sm">Vagas ativas</p>
          <strong className="font-heading text-2xl">{summary?.active_jobs ?? 0}</strong>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-200">
          <p className="text-slate-500 font-bold text-sm">Créditos avulsos</p>
          <strong className="font-heading text-2xl">{summary?.job_credits ?? 0}</strong>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-200">
          <p className="text-slate-500 font-bold text-sm">Créditos destaque</p>
          <strong className="font-heading text-2xl">{summary?.featured_credits ?? 0}</strong>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-200">
          <p className="text-slate-500 font-bold text-sm">Plano</p>
          <strong className="font-heading text-lg">
            {summary?.plan_active ? `${summary.plan_code} (${summary.plan_max_jobs} vagas)` : "Nenhum ativo"}
          </strong>
        </div>
      </section>

      <div className="mb-6">
        <Link to="/planos" className="text-emerald-700 font-extrabold" data-testid="dash-buy-plan">+ Comprar créditos ou plano</Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-20 text-slate-500"><Loader2 className="animate-spin" size={32} /></div>
      ) : jobs.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 border border-slate-200 text-center" data-testid="no-company-jobs">
          <strong className="text-lg font-heading">Você ainda não publicou vagas.</strong>
          <p className="text-slate-600 mt-2">Clique em "Publicar vaga" para começar.</p>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="company-jobs-list">
          {jobs.map((j) => (
            <div key={j.id} className="bg-white rounded-2xl p-6 border border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <StatusBadge status={j.status} />
                  {j.featured && <span className="bg-emerald-100 text-emerald-800 text-xs font-extrabold px-3 py-1 rounded-full flex items-center gap-1"><Star size={12} /> Destaque</span>}
                  {j.pending_changes && <span className="bg-blue-100 text-blue-800 text-xs font-extrabold px-3 py-1 rounded-full">Alteração solicitada</span>}
                  <span className="bg-blue-100 text-blue-800 text-xs font-extrabold px-3 py-1 rounded-full">{j.cidade}</span>
                </div>
                <h3 className="font-heading text-lg font-extrabold text-slate-950">{j.cargo}</h3>
                <p className="text-slate-600">{j.tipo} • {j.salario}</p>
              </div>
              <div className="flex gap-2 flex-wrap self-start">
                {(j.status === "approved" || j.status === "pending") && !j.pending_changes && (
                  <button data-testid={`request-change-${j.id}`} onClick={() => setEditJob(j)}
                    className="text-slate-700 hover:bg-slate-100 border border-slate-200 px-3 py-2 rounded-lg flex items-center gap-1 font-bold">
                    <Pencil size={16} /> Solicitar alteração
                  </button>
                )}
                {j.status !== "closed" && (
                  <button data-testid={`close-job-${j.id}`} onClick={() => close(j.id)}
                    className="text-amber-700 hover:bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg flex items-center gap-1 font-bold">
                    <Power size={16} /> Encerrar
                  </button>
                )}
                <button data-testid={`delete-job-${j.id}`} onClick={() => remove(j.id)}
                  className="text-red-600 hover:bg-red-50 border border-red-200 px-3 py-2 rounded-lg flex items-center gap-1 font-bold">
                  <Trash2 size={16} /> Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
